import { Icon } from "@fluentui/react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppRoutes } from "../../Constants/AppRoutes";
import { ISensor } from "../../Models/ISensor";
import { ISentinelRich } from "../../Models/ISentinel";
import { IAPIResponse } from "../../Services/AjaxService";
import ApiService from "../../Services/ApiService";
import { getSensorIconName } from "../../Utils/SensorMaps";
import { ToastMessage } from "../../Utils/UIMessages";
import IconButtonItem from "../IconButtonItem/IconButtonItem";
import VideoDialog, { useVideoDialog } from "../VideoDialog/VideoDialog";
import "./MonitoringCardSmartBed.scss";
import { developerUI } from "../../Pages/OptionsPage/OptionsPage";
import { JSONPrint } from "../../Utils/Generic";
import WalkieTalkieControls from "../WalkieTalkieControls/WalkieTalkieControls";
import { getOfflineTooltipContent, isSensorOffline } from "../../Utils/SensorStatus";
import DeviceCard, {
  DEVICE_SILENT_TOOLTIP,
  DeviceCardOfflineMark,
  isCardSensor,
  useDeviceCardState,
} from "../DeviceCard/DeviceCard";
import DeviceDetails from "../DeviceDetails/DeviceDetails";
import { roomLabel, sensorName, nameLabel } from "../../Utils/DeviceTexts";

export interface IMonitoringCardSmartBedProps {
  sentinel: ISentinelRich;
  index: number;
}

const MonitoringCardSmartBed = (props: IMonitoringCardSmartBedProps) => {
  const navigate = useNavigate();
  const [lightOn, setLightOn] = useState<boolean>(false);
  const video = useVideoDialog();
  // pannello «Dettagli» aperto: elemento toccato e, se e' un tondo, il suo sensore
  const [details, setDetails] = useState<{ target: HTMLElement; sensor?: ISensor } | null>(null);
  const state = useDeviceCardState(props.sentinel);

  const hasCamera = !!props.sentinel.cameraUrl && props.sentinel.cameraUrl.trim() !== "";
  const room = roomLabel(props.sentinel.patient.room);

  const name = nameLabel(props.sentinel.patient.identifier);

  const setLight = (on: boolean) => {
    ApiService.SentinelController.UpdateLedStatus(
      props.sentinel.id.toString(),
      on,
      (response: IAPIResponse) => {
        if (response.error === null) {
          setLightOn(on);
          ToastMessage("success", (on ? "Led acceso sul dispositivo: " : "Led spento sul dispositivo: ") + name);
        } else {
          ToastMessage(
            "error",
            (on ? "Impossibile accendere il led sul dispositivo: " : "Impossibile spegnere il led sul dispositivo: ") + name
          );
        }
        setTimeout(() => {
          setLightOn(false);
        }, 5000);
      }
    );
  };

  return (
    <DeviceCard sentinel={props.sentinel} state={state} kind="bed">
      <div className="bed-card-head">
        <button
          type="button"
          className="device-card-icon-button"
          title="Dettagli"
          aria-label={"Dettagli di " + name}
          onClick={(e) => setDetails({ target: e.currentTarget })}
        >
          <img src="./assets/bed-monitoring-icon.png" alt="Letto" className="bed-card-icon" />
          <DeviceCardOfflineMark state={state} />
        </button>

        <div className="device-card-buttons">
          {props.sentinel.enabled && (
            <>
              {/* Ascolta e Parla solo se il letto ha il modulo vocale (Setup, matita di Audio) */}
              {props.sentinel.audioEnabled && (
                <WalkieTalkieControls
                  sentinelId={props.sentinel.id}
                  compactMode={false}
                  activatedBy="manual"
                  unavailableLabel={state.silent ? DEVICE_SILENT_TOOLTIP : undefined}
                />
              )}
              <IconButtonItem
                iconName={lightOn ? "VideoLightOff" : "Flashlight"}
                tooltip={state.silent ? DEVICE_SILENT_TOOLTIP : lightOn ? "Spengi led" : "Accendi led"}
                disabled={state.silent}
                onClick={() => setLight(!lightOn)}
              />
              {/* la telecamera e' a parte: il video puo' funzionare anche se il letto non risponde */}
              {hasCamera && <IconButtonItem iconName="Video" tooltip="Visualizza live" onClick={video.show} />}
              <span className="device-card-sep" />
            </>
          )}
          <IconButtonItem
            iconName="Settings"
            tooltip="Configura Letto"
            onClick={() => {
              navigate(
                AppRoutes.APP_CONFIGURE_PAGE +
                  "?sentinelId=" +
                  props.sentinel.id
              );
            }}
          />
        </div>
      </div>

      <div className="bed-card-label">
        <div className="device-card-name">{name}</div>
        <div className="bed-card-row">
          {room !== "" && <span>Stanza: {room}</span>}
          <span>ID Letto: {String(props.sentinel.id)}</span>
        </div>
      </div>

      <div className="bed-card-sensors">
        {props.sentinel.sensors.filter(isCardSensor).map((x: ISensor) => {
          const off = !x.enabled || !props.sentinel.enabled;
          const offline = !off && isSensorOffline(x);
          const hit = state.alarmTypes.includes(x.type);
          // allarme di prova: giallo come la fascia PROVA, non rosso
          const test = !hit && state.testTypes.includes(x.type);
          // nome di sempre; se non risponde, il suggerimento di sempre con l'ultima comunicazione
          const label = offline
            ? getOfflineTooltipContent(x, sensorName(x.type))
            : sensorName(x.type) + (off ? " (spento)" : hit ? " (in allarme)" : test ? " (prova)" : "");
          return (
            <button
              type="button"
              key={x.id}
              className={
                "bed-card-sensor" +
                (off ? " off" : "") +
                (offline ? " offline" : "") +
                (hit ? " hit" : "") +
                (test ? " test" : "")
              }
              title={label}
              aria-label={sensorName(x.type) + ": dettagli"}
              onClick={(e) => setDetails({ target: e.currentTarget, sensor: x })}
            >
              <span className="bed-card-sensor-dot">
                <Icon iconName={getSensorIconName(x.type)} />
              </span>
              {off && <img className="bed-card-sensor-deny" src="./assets/denysign.png" alt="" />}
              {offline && !hit && !test && <span className="bed-card-sensor-x" aria-hidden="true" />}
            </button>
          );
        })}
      </div>
      {developerUI() && <div>{JSONPrint(props.sentinel.sensors)}</div>}

      {details && (
        <DeviceDetails
          sentinel={props.sentinel}
          target={details.target}
          sensor={details.sensor}
          alarmTypes={state.alarmTypes}
          testTypes={state.testTypes}
          onDismiss={() => setDetails(null)}
        />
      )}

      {video.open && <VideoDialog sentinel={props.sentinel} name={name} onDismiss={video.close} />}
    </DeviceCard>
  );
};

export default MonitoringCardSmartBed;
